import { useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import PrimaryButton from "../../../components/buttons/PrimaryButton"
import SecondaryButton from "../../../components/buttons/SecondaryButton"
import FullScreenLayout from "../../../components/FullScreenLayout"
import LoadingSpinner from "../../../components/Loader"
import { useAuth } from "../../../utils/context/AuthContext"
import { ExpertRole } from "../../../utils/Enums"
import { handlePurchasePriority } from "../../../utils/Functions"
import SortingItemTable from "../../shopify-purchases/components/SortingItemTable"
import { useGetAllSortingItems } from "../../shopify-purchases/hooks/useGetAllSortingItems"
import AdminBox from "../components/AdminBox"
import AppointmentsBox from "../components/AppointmentsBox"
import BookAppointmentBox from "../components/BookAppointmentBox"
import ExpertsBox from "../components/ExpertsBox"
import MostRecentPlanBox from "../components/MostRecentPlanBox"
import NotesBox from "../components/NotesBox"
import { PatientStatsBox } from "../components/PatientStatsBox"
import PrivateDocumentsBox from "../components/PrivateDocumentsBox"
import { TestAndAssessmentBox } from "../components/TestAndAssessmentBox"
import UploadsBox from "../components/UploadsBox"
import { useGetAppointmentsByEmail } from "../hooks/useGetAppointmentsByEmail"
import { useGetBloodTracker } from "../hooks/useGetBloodTracker"
import { useGetDnaTracker } from "../hooks/useGetDnaTracker"
import { useGetHealthPlans } from "../hooks/useGetHealthPlans"
import { useGetMealPlans } from "../hooks/useGetMealPlans"
import { useGetPatientById } from "../hooks/useGetPatientById"
import { useGetSurveyData } from "../hooks/useGetSurveyData"
import { usePatientsStats } from "../hooks/usePatientsStats"

const PatientPage = () => {
    const { auth } = useAuth()
    const role = auth.role

    const location = useLocation();
    const navigate = useNavigate()

    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get("patient_id");

    const { patient, isLoading } = useGetPatientById(patientId);
    const { dnaTracker, dnaPickupDate, isLoading: isDnaLoading } = useGetDnaTracker(patientId)
    const { bloodTracker, bloodPickupDate, isLoading: isBloodLoading } = useGetBloodTracker(patientId)
    const { lastSurveyUpdate, surveysCompleted, isLoading: isHealthAssessmentLoading } = useGetSurveyData(patientId)
    const { mealPlans, isLoading: isMealPlanLoading } = useGetMealPlans(patientId)
    const { healthPlans, isLoading: isHealthPlanLoading } = useGetHealthPlans(patientId)
    const { appointments, isLoading: isAppointmentsLoading, error: appointmentsError } = useGetAppointmentsByEmail(patient?.email || null)
    const { sortingItems, isLoading: isSortingLoading, error: sortingError } = useGetAllSortingItems()
    const patientStats = usePatientsStats(patient)

    const patientRelatedItems = useMemo(() => {
        return sortingItems.filter(item =>
            item.buyer?.toLowerCase() === patient?.email ||
            item.redeemed_by?.toLowerCase() === patient?.email
        )
    }, [sortingItems, patient])

    if (isLoading) {
        return (
            <FullScreenLayout title="Caricamento...">
                <div className="w-full h-full flex flex-col items-center justify-center">
                    <div>
                        Attendi un attimo... <br />
                        Stiamo caricando i dati del paziente
                    </div>
                </div>
            </FullScreenLayout>
        );
    }

    if (!patient) {
        return (
            <FullScreenLayout title="Paziente non trovato">
                <div className="w-full h-full flex flex-col items-center justify-center">
                    <div>
                        Paziente non trovato
                    </div>
                    <button
                        onClick={() => navigate("/platform-experts/customers")}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
                    >
                        Torna a clienti
                    </button>
                </div>
            </FullScreenLayout>
        );
    }

    const onCreateMealPlanClick = () => {
        const lastHealthPlan = healthPlans?.[0];

        const hasFood = lastHealthPlan?.data.food_suggested ? "true" : "false";
        const hasIntegrators = lastHealthPlan?.data.integrators_suggested ? "true" : "false";

        navigate(`meal-plan-page?patient_id=${patient.user_id}&hasFood=${hasFood}&hasIntegrators=${hasIntegrators}`);
    };

    const onNavigateToMealPlanClick = () => {
        const lastHealthPlan = healthPlans ? healthPlans[0] : null;

        const hasFood = lastHealthPlan?.data.food_suggested ? "true" : "false";
        const hasIntegrators = lastHealthPlan?.data.integrators_suggested ? "true" : "false";

        navigate(`view-meal-plan-page?patient_id=${patient.user_id}&hasFood=${hasFood}&hasIntegrators=${hasIntegrators}`)
    };

    // ********************************************************************************
    
    return (
        <FullScreenLayout
            title={`${patient.user_name} ${patient.user_family_name}`}
            backButtonText="Torna a clienti"
            onBack={() => navigate("/platform-experts/customers")}
            showBackButton={true}
        >
            <div className="w-full flex flex-col space-y-[20px]">
                {/* Patient Info Header - Mobile First */}
                <div className="w-full flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
                    <div className="flex flex-col">
                        <div className="flex">
                            <div className="font-bold text-[22px] leading-[25px] text-holifya-blue">{handlePurchasePriority(patient)}</div>
                        </div>
                        <div className="mt-[10px] font-normal text-[16px] leading-[33px]">{patient.email} {role !== ExpertRole.Nutritionist && `|| ${patient.user_mobile_phone}`}</div>
                    </div>
                    
                    {/* Action Buttons - Desktop Only */}
                    {role !== ExpertRole.Admin && (
                        <div className="hidden md:flex space-x-[20px]">
                            {role !== ExpertRole.Nutritionist && (
                                <div className="w-[300px] h-[50px]">
                                    <SecondaryButton
                                        text={`Crea ${healthPlans && healthPlans.length > 0 ? "nuovo" : ""} Health Plan`}
                                        pointer={true}
                                        disabled={false}
                                        uppercase={false}
                                        onClick={() => navigate(`health-plan-page?patient_id=${patient.user_id}`)} />
                                </div>
                            )}
                            <div className="w-[300px] h-[50px]">
                                <PrimaryButton
                                    text={`Crea ${mealPlans && mealPlans.length > 0 ? "nuovo" : ""} Meal Plan`}
                                    pointer={true}
                                    disabled={false}
                                    uppercase={false}
                                    onClick={onCreateMealPlanClick}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile Notice - Show only on mobile */}
                {role !== ExpertRole.Admin && (
                    <div className="md:hidden bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                            <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h3 className="text-sm font-medium text-blue-800">
                                    Funzionalità desktop
                                </h3>
                                <p className="text-sm text-blue-700 mt-1">
                                    Per creare Health Plan e Meal Plan, accedi da desktop per una migliore esperienza.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content - Mobile First */}
                <div className="w-full flex flex-col space-y-[35px] md:flex-row md:justify-between md:space-y-0">
                    {/* Left Column - Main Content */}
                    <div className="w-full md:w-[60%] md:pr-[5%] flex flex-col space-y-[35px]">
                        <TestAndAssessmentBox
                            patient={patient}
                            lastSurveyUpdate={lastSurveyUpdate}
                            dnaTracker={dnaTracker}
                            bloodTracker={bloodTracker}
                            isHealthAssessmentLoading={isHealthAssessmentLoading}
                            isDnaLoading={isDnaLoading}
                            isBloodLoading={isBloodLoading}
                        />
                        {
                            isHealthPlanLoading ? <LoadingSpinner /> : healthPlans && healthPlans.length > 0 && <MostRecentPlanBox planType="Health Plan" plan={healthPlans[0]} onClick={() => navigate(`view-health-plan-page?patient_id=${patient.user_id}`)} />
                        }
                        {
                            isMealPlanLoading ? <LoadingSpinner /> : mealPlans && mealPlans.length > 0 && <MostRecentPlanBox planType="Meal Plan" plan={mealPlans[0]} onClick={onNavigateToMealPlanClick} />
                        }
                        <UploadsBox patient={patient} />
                        <PrivateDocumentsBox patient={patient} />
                        <NotesBox patient={patient} />
                        { role === ExpertRole.Admin && <AdminBox patient={patient}/>}
                        { role !== ExpertRole.Nutritionist && <ExpertsBox patient={patient} role={role!} />}
                        {patientRelatedItems.length > 0 && (
                            <div className="space-y-[25px]">
                                <div className="font-bold text-[16px] leading-[33px]">Acquisti relativi al paziente</div>
                                <SortingItemTable items={patientRelatedItems} patientEmail={patient.email} />
                            </div>
                        )}
                    </div>
                    
                    {/* Right Column - Sidebar (Desktop) / Bottom (Mobile) */}
                    <div className="w-full md:w-[40%] space-y-[25px]">
                        {role === ExpertRole.Admin && <PatientStatsBox stats={patientStats} />}
                        {(role === ExpertRole.Nutritionist || role === ExpertRole.Physician || role === ExpertRole.Specialist) && <BookAppointmentBox patient={patient} />}
                        <AppointmentsBox appointments={appointments} />
                    </div>
                </div>
            </div>
        </FullScreenLayout>
    )
}

export default PatientPage
